import { EnvironmentBadge, environmentBadge } from './environment-badge';

/**
 * Marks the browser tab of every environment that is not production.
 *
 * A demo tab and a local tab are otherwise the same tab: same brand mark, same title, and the
 * sidebar — the one place the environment shows — is a glance away and gone entirely when the bar
 * is collapsed. The tab icon is the one thing always on screen, so that is where the environment
 * is said.
 *
 * The mark itself is kept — the Connect C in its orbit ring — and a badge is set into its
 * bottom-right corner: a filled circle in the environment's own colour with its initial inside.
 * The colour is what does the work at 16px, where the tab is a grey square and the letter is a few
 * pixels across; the letter names the environment at 32px and up — a pinned tab, the history, a
 * bookmark list, and the tab itself on a retina screen.
 *
 * Production is left alone: it wears the real mark, and anything else is the odd one out. Which
 * environments those are, and in what colour, is `environment-badge.ts` — shared with the sidebar,
 * which says the same thing in the space it has.
 */

/**
 * The mark the badge is set into: the icon the tab is actually served, so the badged version is the
 * same artwork with something added rather than a second, differently-branded one.
 *
 * `favicon.ico` and not one of the PNGs under `assets/` — those hold two older marks, and the links
 * to them in `index.html` are commented out for that reason.
 */
const BASE_ICON = 'favicon.ico';

/** Canvas the icon is composed at — three times the 64px a retina tab asks for at most. */
const SIZE = 64;

/** The white of the mark's own strokes, which the badge is ringed in. */
const MARK_STROKE = '#ffffff';

/**
 * The mark with the environment's badge on it, as a PNG data URL.
 *
 * Null when the icon cannot be composed — no 2D context, or the mark failed to load — in which
 * case the tab keeps the favicon it was served. A wrong icon would be worse than an unmarked one.
 */
export async function renderBadgeIcon(badge: EnvironmentBadge, doc: Document = document): Promise<string | null> {
  const canvas = doc.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const context = canvas.getContext('2d');
  if (!context) {
    return null;
  }

  const mark = await loadImage(BASE_ICON, doc);
  if (!mark) {
    return null;
  }
  context.drawImage(mark, 0, 0, SIZE, SIZE);

  // Bottom-right, and big enough to read as a colour rather than a speck once the whole icon is
  // 16px: at 28% of the width in radius the badge is about 9px across there, and still leaves most
  // of the mark uncovered.
  const radius = SIZE * 0.28;
  const centre = SIZE - radius - 1;

  // Ringed in the white the C and its orbit are drawn in, so the badge reads as set into the mark
  // rather than dropped on top of it, and keeps its edge wherever it meets a white stroke.
  context.beginPath();
  context.arc(centre, centre, radius + 2, 0, Math.PI * 2);
  context.fillStyle = MARK_STROKE;
  context.fill();

  context.beginPath();
  context.arc(centre, centre, radius, 0, Math.PI * 2);
  context.fillStyle = badge.background;
  context.fill();

  context.fillStyle = MARK_STROKE;
  // The heaviest weight the system face carries: at badge size a 700 glyph thins out to almost
  // nothing against the fill behind it.
  context.font = `900 ${Math.round(radius * 1.35)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  context.textAlign = 'center';
  // A cap-height glyph sits high in its line box, so the optical centre is below the geometric one.
  context.textBaseline = 'middle';
  context.fillText(badge.label, centre, centre + radius * 0.06);

  return canvas.toDataURL('image/png');
}

/** Resolves to null rather than rejecting: a missing mark is a reason to do nothing, not to throw. */
function loadImage(src: string, doc: Document): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new (doc.defaultView?.Image ?? Image)();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

/**
 * Puts the environment's badge in the tab, if the environment has one.
 *
 * Rewrites the existing `rel="icon"` rather than appending a second one: browsers pick among
 * competing icon links by their own rules, and the point here is that there is nothing to pick.
 * `render` is a parameter so the wiring can be exercised without a canvas.
 *
 * Asynchronous because the mark has to be fetched before it can be drawn on, so the tab swaps its
 * icon a beat after the page appears rather than with it.
 */
export async function applyEnvironmentFavicon(
  name: string,
  doc: Document = document,
  render: (badge: EnvironmentBadge, doc: Document) => Promise<string | null> = renderBadgeIcon
): Promise<void> {
  const badge = environmentBadge(name);
  if (!badge) {
    return;
  }

  const href = await render(badge, doc);
  if (!href) {
    return;
  }

  const link = doc.querySelector<HTMLLinkElement>('link[rel="icon"]') ?? doc.head.appendChild(doc.createElement('link'));
  link.rel = 'icon';
  link.type = 'image/png';
  link.href = href;
}
